import React, { FC, useState } from 'react'
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { FormatInsertRuleForm } from '@/apps/legacy/components/molecules/FormatInsertRuleForm'
import { FormatInsertRuleList } from '@/components/molecules/FormatInsertRuleList'
import {
  useFormatInsertRulesQuery,
  useCreateFormatInsertRuleMutation,
  useUpdateFormatInsertRuleMutation,
  useToggleFormatInsertRuleMutation,
  useDeleteFormatInsertRuleMutation,
} from '@/features/internal/format-insert-rules/hooks/useFormatInsertRulesQuery'
import { useToast } from '@/shared/contexts/ToastContext'
import { FormatInsertRule } from '@/features/internal/format-insert-rules/types/formatInsertRule'
import {
  CustomButton,
  CancelButton,
  ActionButton,
} from '@/apps/legacy/components/portal/CustomButton'
import { PortalLayout } from '@/apps/legacy/components/portal/PortalLayout'
import { PortalPageHeader } from '@/apps/legacy/components/portal/PortalPageHeader'

type DialogMode = 'create' | 'edit' | 'delete' | null

export const FormatInsertSettingsPage: FC = () => {
  const { showToast } = useToast()
  const { data, isLoading: loading, error } = useFormatInsertRulesQuery()
  const rules = data ?? []
  const createMutation = useCreateFormatInsertRuleMutation()
  const updateMutation = useUpdateFormatInsertRuleMutation()
  const toggleMutation = useToggleFormatInsertRuleMutation()
  const deleteMutation = useDeleteFormatInsertRuleMutation()

  const [dialogMode, setDialogMode] = useState<DialogMode>(null)
  const [selectedRule, setSelectedRule] = useState<FormatInsertRule | null>(
    null
  )
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleCreate = () => {
    setSelectedRule(null)
    setDialogMode('create')
  }

  const handleEdit = (rule: FormatInsertRule) => {
    setSelectedRule(rule)
    setDialogMode('edit')
  }

  const handleDelete = (id: string) => {
    const rule = rules.find((r) => r.id === id)
    if (rule) {
      setSelectedRule(rule)
      setDialogMode('delete')
    }
  }

  const handleSubmitRule = async (ruleData: FormatInsertRule) => {
    try {
      setIsSubmitting(true)

      if (dialogMode === 'create') {
        await createMutation.mutateAsync(ruleData)
      } else if (dialogMode === 'edit' && selectedRule?.id) {
        await updateMutation.mutateAsync({
          id: selectedRule.id,
          rule: ruleData,
        })
      }

      setDialogMode(null)
      setSelectedRule(null)
    } catch (_err) {
      // エラーは mutation 内で処理される
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleConfirmDelete = async () => {
    if (!selectedRule?.id) return

    try {
      setIsSubmitting(true)
      await deleteMutation.mutateAsync(selectedRule.id)
      setDialogMode(null)
      setSelectedRule(null)
    } catch (_err) {
      // エラーは mutation 内で処理される
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleToggleActive = async (id: string) => {
    try {
      const result = await toggleMutation.mutateAsync(id)
      showToast(result.message, 'success')
    } catch (_err) {
      showToast('ルールの状態変更に失敗しました', 'error')
    }
  }

  const handleCloseDialog = () => {
    if (!isSubmitting) {
      setDialogMode(null)
      setSelectedRule(null)
    }
  }

  if (error) {
    return (
      <PortalLayout>
        <Typography color="error">{error.message}</Typography>
      </PortalLayout>
    )
  }
  return (
    <PortalLayout>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <PortalPageHeader title="フォーマット自動挿入設定" />
        <ActionButton
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
        >
          新規ルール追加
        </ActionButton>
      </Box>

      <FormatInsertRuleList
        rules={rules}
        loading={loading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onToggleActive={handleToggleActive}
      />

      {/* ルール作成・編集ダイアログ */}
      <Dialog
        open={dialogMode === 'create' || dialogMode === 'edit'}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        disableEscapeKeyDown={isSubmitting}
      >
        <DialogTitle>
          {dialogMode === 'create' ? 'ルール作成' : 'ルール編集'}
        </DialogTitle>
        <DialogContent>
          <FormatInsertRuleForm
            rule={selectedRule || undefined}
            onSubmit={handleSubmitRule}
            onCancel={handleCloseDialog}
            isLoading={isSubmitting}
          />
        </DialogContent>
      </Dialog>

      {/* 削除確認ダイアログ */}
      <Dialog
        open={dialogMode === 'delete'}
        onClose={handleCloseDialog}
        disableEscapeKeyDown={isSubmitting}
      >
        <DialogTitle>ルール削除確認</DialogTitle>
        <DialogContent>
          <DialogContentText>
            「{selectedRule?.name}」を削除しますか？
            <br />
            この操作は取り消すことができません。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <CancelButton onClick={handleCloseDialog} disabled={isSubmitting}>
            キャンセル
          </CancelButton>
          <CustomButton
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={isSubmitting}
          >
            {isSubmitting ? '削除中...' : '削除'}
          </CustomButton>
        </DialogActions>
      </Dialog>
    </PortalLayout>
  )
}

export default FormatInsertSettingsPage
